// src/hooks/useNotificationAudio.js
import { useEffect, useRef, useState } from 'react';
import soundUrl from '../assets/sound/s2.wav';

// Singleton audio instance (shared across all components)
let globalAudio = null;
let isUnlocked = false;
let lastPlayTime = 0;
const PLAY_COOLDOWN_MS = 1500;

export function useNotificationAudio() {
  const [audioReady, setAudioReady] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    // Initialize audio once globally
    if (!globalAudio) {
      globalAudio = new Audio(soundUrl);
      globalAudio.volume = 0.6;
      globalAudio.preload = 'auto';

      // Load the audio file
      globalAudio.load();

      audioRef.current = globalAudio;
      console.log('🔊 Audio initialized and preloaded');
    } else {
      audioRef.current = globalAudio;
    }

    // Simple unlock on first user interaction
    const unlockAudio = () => {
      if (isUnlocked) return;

      isUnlocked = true;
      setAudioReady(true);
      console.log('✅ Audio unlocked and ready');

      // Remove listeners after unlock
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    };

    // Add event listeners if not unlocked
    if (!isUnlocked) {
      document.addEventListener('click', unlockAudio, {
        once: true,
        passive: true
      });
      document.addEventListener('keydown', unlockAudio, {
        once: true,
        passive: true
      });
      document.addEventListener('touchstart', unlockAudio, {
        once: true,
        passive: true
      });
    } else {
      setAudioReady(true);
    }

    // Cleanup
    return () => {
      if (!isUnlocked) {
        document.removeEventListener('click', unlockAudio);
        document.removeEventListener('keydown', unlockAudio);
        document.removeEventListener('touchstart', unlockAudio);
      }
    };
  }, []);

  // Play notification sound
  const playNotification = async () => {
    if (!audioRef.current || !isUnlocked) return false;

    const now = Date.now();
    if (now - lastPlayTime < PLAY_COOLDOWN_MS) return false;
    lastPlayTime = now;

    try {
      audioRef.current.currentTime = 0;
      await audioRef.current.play();
      return true;
    } catch (error) {
      console.error('❌ Failed to play notification:', error.message);
      return false;
    }
  };

  return {
    playNotification,
    audioReady,
    isUnlocked
  };
}
